import React, { Children } from 'react';
import ImageAvatar from 'ui/image/image-avatar';
import { FormField } from 'ui/form/form-field';
import ChildField, { wantsFormData } from 'ui/form/child-field';

export function countFormFields(children: React.ReactChildren) {
  let count = 0;
  Children.forEach(children, child => {
    if (child && child.props) {
      if (child.type.formfield) {
        count++;
      } else if (child.props && child.props.children) {
        count += countFormFields(child.props.children);
      }
    }
  });
  return count;
}
export function getFirstFormField(children: React.ReactChildren) {
  let formField;
  Children.forEach(children, child => {
    if (!formField && child && child.props) {
      if (child.type.formfield) {
        formField = child;
      } else if (child.props && child.props.children) {
        formField = getFirstFormField(child.props.children);
      }
    }
  });

  return formField;
}

export function isFormField(child: React.ReactChild) {
  const type = child && child.type && (child.type.wrapType || child.type);
  return type && type.formfield;
}

export function hasChildren(child: React.ReactChild) {
  return child && child.props && child.props.children;
}

export function renderIcon(icon, styles) {
  if (!icon) return null;
  if (React.isValidElement(icon)) return <div className={styles.materialIcons}>{icon}</div>;
  else return <ImageAvatar small className={styles.fieldIcon} image={icon} />;
}

export function getFieldValues(fields: { [string]: FormField }): { [string]: any } {
  const value = {};
  for (let name in fields) {
    let childValue = fields[name] && fields[name].getValue();
    if (childValue !== null && childValue !== undefined) value[name] = childValue;
  }
  return value;
}

export const isValue = value =>
  (value || value === 0 || value === false || value === '' || value === []) && value !== undefined;

export function getErrorsFromChildren(props, formData): string | false {
  const { children } = props;
  let ret = false;

  if (children) {
    React.Children.forEach(children, child => {
      if (child.type && child.type.getErrors) {
        /**
         * Contains filtered formData.
         * Conditional fields (with no name) needs no filtering.
         */
        const passFormData = props.name ? (formData || {})[props.name] : formData;
        const error = child.type.getErrors(child.props, passFormData || {});

        if (error) ret = error;
      }
    });
  }

  return ret;
}

export function renderChildren(children, { handleRef, props, handleChange }) {
  const render = (child: React.ReactChild, index: number) => {
    if (isFormField(child)) {
      const name = child.props.name;

      if (!isValue(name)) {
        return (
          <div className="text-error" key={index}>
            {child.type.displayName || 'Field'} is missing name attribute
          </div>
        );
      }

      return (
        <ChildField
          child={child}
          childRef={handleRef}
          formData={props.formData}
          key={index}
          name={name}
          noValidation={props.noValidation}
          onChange={handleChange}
          tiny={props.tiny}
          value={props.value && props.value[name]}
        />
      );
    } else if (wantsFormData(child)) {
      return (
        <child.type {...child.props} formData={props.formData} key={index}>
          {Children.map(child.props.children, render)}
        </child.type>
      );
    } else if (hasChildren(child)) {
      return (
        <child.type {...child.props} key={index}>
          {Children.map(child.props.children, render)}
        </child.type>
      );
    }
    return child || null;
  };

  return Children.map(children, render) || null;
}
